import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';
import { Field, formValues, reduxForm, change } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import TPGrid from 'components/TP-UI/TPGrid';
import TPAlert from 'components/TP-UI/TPAlert';
import TPButton from 'components/TP-UI/TPButton';
import TPErrorMessage from 'components/TP-UI/TPErrorMessage';
import TPCard from 'components/TP-UI/TPCard';
import TPSelect from 'components/TP-UI/TPSelect';
import TPCardContent from 'components/TP-UI/TPCard/components/TPCardContent';
import TPCardTitle from 'components/TP-UI/TPCard/components/TPCardTitle';
import TPCardDescription from 'components/TP-UI/TPCard/components/TPCardDescription';
import {
  ACCOUNT_TYPES,
  ACCOUNT_TYPE_TRADER,
  ACCOUNT_TYPE_PRO,
} from 'modules/tradingCalculators/constants';
import CurrencySelect from 'modules/common/CurrencySelect';
import AmountTextField from 'modules/common/AmountTextField';
import { required } from 'utils/validation/fieldValidationRules';
import Results from '../Results';
import SymbolGroupForm from '../SymbolGroupForm';
import { isAsicRegulator } from 'helpers/regulators';

import useStyles from './styles';

const MarginCalculatorForm = ({
  loading,
  isAuHub,
  leverageDisabled,
  symbols,
  currencies,
  baseCurrency,
  results,
  form,
  error = null,
  contractSize,
  handleSubmit,
  isPublic,
  symbolLeverage,
  isRetailUser,
  accountType,
  regulator,
}) => {
  const { t } = useTranslation('tradingCalculators');
  const classes = useStyles();
  const dispatch = useDispatch();
  const accountTypeOptions = useMemo(
    () => ACCOUNT_TYPES.map(({ value, label }) => ({ value, label: t(`fields.${label}`) })),
    [t],
  );
  const descriptionItems = useMemo(
    () => [
      [
        {
          dataKey: 'price',
          label: t(`results.price`),
        },
        {
          dataKey: 'currentConversionPrice',
          label: t(`results.currentConversionPrice`),
        },
      ],
      [
        {
          dataKey: 'accountRequiredMargin',
          label: t(`results.accountRequiredMargin`),
        },
      ],
    ],
    [t],
  );

  useEffect(() => {
    if (accountType && ((isPublic && isAsicRegulator(regulator)) || (!isPublic && isRetailUser))) {
      dispatch(change(form, 'accountType', ACCOUNT_TYPE_TRADER));
    }
  }, [dispatch, form, accountType, isPublic, isRetailUser, regulator]);

  return (
    <TPGrid container spacing={3}>
      <TPGrid item>
        <TPAlert variant="info">{t('marginCalculator.description')}</TPAlert>
      </TPGrid>
      <TPGrid item container spacing={2}>
        <TPGrid item xs={12} md={6}>
          <TPCard stretch>
            <TPCardContent>
              <TPCardTitle>{t('calculatorTitle')}</TPCardTitle>
              <TPCardDescription>{t('calculatorDescription')}</TPCardDescription>
              <form noValidate className={classes.form} onSubmit={handleSubmit}>
                <TPGrid container spacing={3} direction="column">
                  {error && (
                    <TPGrid item xs={12}>
                      <TPErrorMessage error={error} size="medium" />
                    </TPGrid>
                  )}
                  <TPGrid item container direction="row" spacing={2}>
                    {isAuHub && !isRetailUser && !isPublic ? (
                      <TPGrid item xs={12} md={6}>
                        <Field
                          label="Account Type"
                          name="accountType"
                          component={TPSelect}
                          options={accountTypeOptions}
                          fullWidth
                          required
                          validate={[required]}
                        />
                      </TPGrid>
                    ) : null}
                    <TPGrid item xs={12}>
                      <SymbolGroupForm form={form} symbols={symbols} contractSize={contractSize} />
                    </TPGrid>
                    <TPGrid item xs={12} md={6}>
                      <Field
                        component={CurrencySelect}
                        id="baseCurrency"
                        name="baseCurrency"
                        label={t('fields.baseCurrency')}
                        placeholder={t('placeholders.baseCurrency')}
                        options={currencies}
                        required
                        fullWidth
                        validate={[required]}
                      />
                    </TPGrid>
                    {leverageDisabled || accountType === ACCOUNT_TYPE_TRADER ? null : (
                      <TPGrid item xs={12} md={6}>
                        <Field
                          component={TPSelect}
                          id="leverage"
                          name="leverage"
                          label={t('fields.leverage')}
                          placeholder={t('placeholders.leverage')}
                          fullWidth
                          options={symbolLeverage}
                          required
                          validate={[required]}
                          disabled={leverageDisabled}
                        />
                      </TPGrid>
                    )}
                    <TPGrid item xs={12} md={6}>
                      <Field
                        component={AmountTextField}
                        id="volumeInLots"
                        name="volumeInLots"
                        label={t('fields.volumeInLots')}
                        placeholder={t('placeholders.volumeInLots')}
                        type="number"
                        currency={baseCurrency}
                        required
                        fullWidth
                        validate={[required]}
                      />
                    </TPGrid>
                    <TPGrid item container justifyContent="flex-end">
                      <TPButton
                        primary
                        type="submit"
                        size="large"
                        loading={loading}
                        fullWidth={isMobileOnly}>
                        {t('buttons.calculate')}
                      </TPButton>
                    </TPGrid>
                  </TPGrid>
                </TPGrid>
              </form>
            </TPCardContent>
          </TPCard>
        </TPGrid>
        <TPGrid item xs={12} md={6}>
          <Results loading={loading} results={results} descriptionItems={descriptionItems} />
        </TPGrid>
      </TPGrid>
    </TPGrid>
  );
};

MarginCalculatorForm.propTypes = {
  form: PropTypes.string.isRequired,
  isAuHub: PropTypes.bool,
  loading: PropTypes.bool,
  leverageDisabled: PropTypes.bool,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  symbols: PropTypes.array.isRequired,
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      flag: PropTypes.string.isRequired,
    }),
  ).isRequired,
  baseCurrency: PropTypes.string,
  contractSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  symbolLeverage: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  isRetailUser: PropTypes.bool,
  regulator: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmit: PropTypes.func.isRequired,
};

export default compose(
  reduxForm({
    initialValues: {
      accountType: ACCOUNT_TYPE_PRO,
    },
  }),
  formValues('symbol'),
  formValues('accountType'),
)(MarginCalculatorForm);
